import type { ThemeOptions } from '@mui/material/styles';
import type { SemanticConfig } from '../types';
import { lightPrimitives } from '../primitives';
import { buildSemanticPalette } from '../semantic';
import {
    breakpoints,
    getTypographyOptions,
    getComponentOverrides,
    DASHBOARD_FONT,
} from '../shared';

const resolved = lightPrimitives;

const semanticConfig: SemanticConfig = {
    neutral: resolved.woodsmoke,
    primary: resolved.blueribbon,
    success: resolved.shamrock,
    error: resolved.thunderbird,
    warning: resolved.sun,
    accent: resolved.riptide,
};

const semantic = buildSemanticPalette(semanticConfig);

export const lightThemeOptions: ThemeOptions = {
    palette: {
        mode: 'light',

        // --- Legacy palette (unchanged from original theme.ts) ---
        primary: {
            main: '#247FFF',
            light: '#579DFF',
            dark: '#0B4EAC',
        },
        secondary: {
            main: '#FFAB00',
            dark: '#db8a11',
        },
        background: {
            default: '#FAFBFF',
        },
        text: {
            primary: '#43485C',
            secondary: '#727687',
        },
        error: {
            main: '#FD6E4E',
            dark: '#4B2016',
        },
        warning: {
            main: '#FFAB00',
            light: '#FFAB001A',
        },
        info: {
            main: '#43485C',
        },
        success: {
            main: '#38CB89',
            dark: '#255744',
        },
        hypertracker: {
            main: '#15C8B1',
            light: '#8CF7E0',
            dark: '#12AA96',
        },
        based: {
            main: '#FF6940',
            light: '#FF7650',
        },
        divider: '#EDF0F7',
        hyperLiquidColor: '#15C8B1',
        hyperLiquidColor2: '#8CF7E0',
        hyperLiquidColor3: '#9BE5C4',
        blueTelegram: '#29ABEE',
        backgrounds: {
            bg1: '#ffffff',
            bg2: '#EDF0F7',
            bg3: '#ffffff',
            bg4: '#ffffff',
            bg5: '#262731',
            bg6: '#F5F7FB',
            buttonSelectBg: '#377DFF',
            sideBarBackground: '#005BDA',
            layoutHeaderBg: '#0B4EAC',
            pageHeaderBackground: '#ffffff',
            progressDefaultBg: '#E7E8EB',
            loginFormBg: '#26272C',
            loginInputsBg: '#3B3C46',
            tableHeadBg: '#F0F6FF',
            tableRowBg: '#FAFBFF',
            tableFooterBg: '#EEF1F5',
            successDark100: '#2F8C64',
            errorDark100: '#743D33',
            errorDark50: '#BE5841',
            modalBodyBg: '#FFFFFF',
            modalBlurredBg: '#1A1B234D',
            bullMeterVeryBullishBg: '#1F3D37',
            bullMeterBullishBg: '#2F5950',
            bullMeterSlightlyBullishBg: '#386C62',
            bullMeterVeryBearishBg: '#5E3430',
            bullMeterBearishBg: '#7A423D',
            bullMeterSlightlyBearishBg: '#884D47',
        },
        customText: {
            text1: '#80B2F9',
            text2: '#43485C',
            text3: '#B0B7C3',
            text4: '#FFF',
            text5: '#1a1b23',
            text6: '#001C42',
            text7: '#000000',
            text10: '#707077',
            text11: '#8D8D91',
            drawerTextColor: '#FFFFFF',
            textDark: '#001C42',
        },

        // --- New design-system layer (Figma primitives + semantic) ---
        primitives: resolved,
        semantic,
    },
    typography: getTypographyOptions(DASHBOARD_FONT),
    breakpoints,
    components: getComponentOverrides(false),
};
